import { Box } from '@mui/system';
import React from 'react';
import AtlasSalesExportModal, {
  SalesDropdownOption
} from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';
import { EXPORT_TYPES_DROPDOWN_OPTIONS } from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';
import { EXPORT_TYPES } from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum,
  DorisTableFieldColumnNamesEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { usePaginatedProductData } from 'src/components/AtlasV2Components/utils/tableUtils';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { BEACON_KEY_METRIC_CARD_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';

import { getAtlasSalesV2Treatment } from 'src/utils/app';
import { useCategoryEntity, useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';

const useRatingsDropdownOptions = (): SalesDropdownOption[] => {
  const { isAllCategoriesEntity } = useCategoryEntity();
  const entityType = useMainEntityType();

  switch (entityType) {
    case 'brand':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_CATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_SUBCATEGORY]
      ];
    case 'category':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_BRAND],
        ...(isAllCategoriesEntity
          ? [EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_CATEGORY]]
          : []),
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_SUBCATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_BRAND_SUBCATEGORY]
      ];

    case 'subcategory':
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_BRAND]
      ];
    case 'product':
      return [EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_PRODUCT]];
    default:
      return [
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_PRODUCT],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_BRAND],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_CATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_SUBCATEGORY],
        EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_REVIEWS_RATING_BRAND_SUBCATEGORY]
      ];
  }
};

const fields = [
  {
    name: DorisTableFieldColumnNamesEnum.Rating,
    displayName: 'RATING'
  },
  {
    name: DorisTableFieldColumnNamesEnum.ReviewsCount,
    displayName: 'REVIEWS COUNT'
  },
  {
    name: DorisTableFieldColumnNamesEnum.RetailSales,
    displayName: 'RETAIL SALES'
  }
];

/**
 * Shows the ratings for each product in a grid,
 * sorted by retail sales
 */
export default function AtlasRatingsV2ProductGrid() {
  const { isProductEntity } = useProductEntity();
  const dropdownOptions = useRatingsDropdownOptions();

  const {
    enrichedData,
    isLoading,
    isMetadataLoading,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName
  } = usePaginatedProductData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: isProductEntity ? DorisGroupByFieldNameEnum.Week : DorisGroupByFieldNameEnum.Product,
    metricType: DorisMetricTypeEnum.ProductsWithReviewsData,
    fields,
    ...(isProductEntity && { sortFieldName: 'retailSales' }),
    itemsPerPage: 20,
    metadataFetchSize: 100
  });

  return (
    <Box sx={{ marginTop: `${BEACON_KEY_METRIC_CARD_SPACING}px` }}>
      {isProductEntity ? (
        <AtlasV2MetricListContainer
          entityTableHeaderProps={{
            title: 'Rating by Week',
            customExportModal: (props: { open: boolean; close: () => void }) => (
              <AtlasSalesExportModal
                open={props.open}
                onClose={props.close}
                metricType={DorisMetricTypeEnum.ReviewsRating}
                customDropdownOptions={dropdownOptions}
              />
            )
          }}
          indexName="sales"
          sortFieldName="rating"
          fields={[...fields].map(removeComparisonFormatter)}
          headColumnDefinition={getWeekColumnDefinition({
            getTableColumnProps(defaults) {
              return {
                ...defaults,
                style: {
                  ...defaults.style,
                  width: '220px'
                }
              };
            }
          })}
          postProcessData={(data) => {
            return data.sort((a, b) => b.weekId - a.weekId);
          }}
          customDataSource={{
            data: enrichedData,
            isLoading: isLoading || isMetadataLoading,
            getValuesByFieldName: getPagedValuesByFieldName,
            getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
          }}
          useTableDataProps={{
            groupByFieldName: 'weekId',
            filterResponse: () => true,
            itemsPerPage: 100
          }}
          showGrid={false}
        />
      ) : (
        <Flex flexDirection="column" gap={16}>
          <AtlasV2ProductListContainer
            showGrid
            indexName="sales"
            mainFieldName="rating"
            fields={fields}
            entityTableHeaderProps={{
              title: `Rating by Product`,
              customExportModal: (props: { open: boolean; close: () => void }) => (
                <AtlasSalesExportModal
                  open={props.open}
                  onClose={props.close}
                  metricType={DorisMetricTypeEnum.ReviewsRating}
                  customDropdownOptions={dropdownOptions}
                />
              )
            }}
            headColumnDefinition={{
              headerName: 'Product',
              valueFormatter: (_, row: { product: { id: string; title: string } }) => {
                return (
                  <div className="product-col" style={{ width: '580px' }}>
                    <ProductCell stacklineSku={row.product?.id} title={row.product?.title} />
                  </div>
                );
              }
            }}
            customDataSource={{
              data: enrichedData,
              isLoading: isLoading || isMetadataLoading,
              getValuesByFieldName: getPagedValuesByFieldName,
              getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
            }}
            useTableDataProps={{
              itemsPerPage: 300
            }}
            shouldHidePagination
            iconList={[IconsList.export, IconsList.tile, IconsList.table]}
          />
          <Flex justifyContent="flex-end">
            <Pagination
              currentPage={currentPage}
              shouldUseInfinitePagination
              onClickNext={handleNextPage}
              capPage={50}
              onClickPrevious={handlePreviousPage}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
